import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createUUId} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../base/button/button";
import {AddOutlined} from "@mui/icons-material";
import CKEditorTextfield from "../../text-fields/ck-editor-textfield";

const schema = Yup.object().shape({
    toEmail: Yup.array().of(Yup.string().email(ValidateMessages.incorrectType('email'))).min(1, ValidateMessages.required),
    ccEmail: Yup.array().of(Yup.string().email(ValidateMessages.incorrectType('email'))),
    bccEmail: Yup.array().of(Yup.string().email(ValidateMessages.incorrectType('email'))),
    subject: Yup.string().required(ValidateMessages.required),
    body: Yup.string().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const SendEmailDialog = ({open, close, data}) => {
    const [sending, setSending] = useState(false);
    const [state, setState] = useState({includeCc: false, includeBcc: false});
    const idRef = useRef(createUUId(true));

    const initialValues = useMemo(() => ({
        toEmail: data.toEmail,
        ccEmail: data.ccEmail,
        bccEmail: data.bccEmail,
        subject: data.subject,
        body: data.body,
    }), [data]);

    /**
     * Updates the state of the email dialog when the cc and bcc fields are changed.
     */
    useEffect(() => {
        if (open) {
            setState({includeCc: data?.ccEmail?.length > 0, includeBcc: data?.bccEmail?.length > 0});
        }
    }, [data?.bccEmail?.length, data?.ccEmail, open]);

    /**
     * When the email dialog is closed, reset sending states.
     */
    useEffect(() => {
        if (!open) {
            setSending(false);
            setState({includeCc: false, includeBcc: false})
        }
    }, [open]);

    /**
     * Sends the email.
     * @type {(function(*): Promise<void>)}
     */
    const sendEmail = useCallback(async (values) => {
        setSending(true);
        const forApi = {
            to: values.toEmail,
            cc: state.includeCc ? values.ccEmail : null,
            bcc: state.includeBcc ? values.bccEmail : null,
            subject: values.subject,
            body: values.body,
        }
        await new Promise(r => setTimeout(r, 2000));
        // TODO: Call the api to send the email.
        const response = {
            resultFlag: true,
        }
        setSending(false);
        if (response?.resultFlag) {
            close();
        }
    }, [close, state.includeBcc, state.includeCc])

    return (
        <>
            <Dialog open={open} setOpen={close} maxWidth={'sm'}>
                <DialogTitle>
                    Send an Email
                </DialogTitle>
                <DialogContent dividers>
                    <Form
                        onSubmit={sendEmail}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: idRef.current}}
                    >
                        {() => {
                            return (
                                <Grid2 spacing={2} container>
                                    <Grid2 xs={12}>
                                        <MuiRFF.Autocomplete
                                            freeSolo
                                            multiple
                                            disabled={sending}
                                            name={'toEmail'}
                                            label={'Send To'}
                                            required={required.toEmail}
                                            options={[]}
                                        />
                                    </Grid2>
                                    {
                                        state.includeCc &&
                                        <Grid2 xs={12}>
                                            <MuiRFF.Autocomplete
                                                freeSolo
                                                multiple
                                                disabled={sending}
                                                name={'ccEmail'}
                                                label={'CC'}
                                                required={required.ccEmail}
                                                options={[]}
                                            />
                                        </Grid2>
                                    }
                                    {
                                        state.includeBcc &&
                                        <Grid2 xs={12}>
                                            <MuiRFF.Autocomplete
                                                freeSolo
                                                multiple
                                                disabled={sending}
                                                name={'bccEmail'}
                                                label={'BCC'}
                                                required={required.bccEmail}
                                                options={[]}
                                            />
                                        </Grid2>
                                    }
                                    {
                                        (!state.includeCc || !state.includeBcc) &&
                                        <Grid2 xs={12}>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap'}}>
                                                {
                                                    !state.includeCc &&
                                                    <Button
                                                        variant={'text'}
                                                        color={'secondary'}
                                                        size={'small'}
                                                        onClick={() => setState(prevState => ({...prevState, includeCc: true}))}
                                                        sx={{display: 'flex', alignItems: 'center', gap: 0.5,}}
                                                    >
                                                        <AddOutlined className={'icon small'}/> Include CC
                                                    </Button>
                                                }
                                                {
                                                    !state.includeBcc &&
                                                    <Button
                                                        variant={'text'}
                                                        color={'secondary'}
                                                        size={'small'}
                                                        onClick={() => setState(prevState => ({...prevState, includeBcc: true}))}
                                                        sx={{display: 'flex', alignItems: 'center', gap: 0.5,}}
                                                    >
                                                        <AddOutlined className={'icon small'}/> Include Bcc
                                                    </Button>
                                                }
                                            </Box>
                                        </Grid2>
                                    }
                                    <Grid2 xs={12}>
                                        <MuiRFF.TextField
                                            disabled={sending}
                                            name={'subject'}
                                            label={'Subject'}
                                            required={required.subject}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <CKEditorTextfield
                                            form
                                            disabled={sending}
                                            name={'body'}
                                            required={required.body}
                                        />
                                    </Grid2>
                                </Grid2>
                            )
                        }}
                    </Form>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            disabled={sending}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => close()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        <Button
                            disabled={sending}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={idRef.current}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {sending ? "Sending..." : 'Send'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}


export default SendEmailDialog;